import { Component, computed, effect, inject, type Signal, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Calendar, ChevronDown, ChevronUp, LucideAngularModule, Tag, User, Video } from 'lucide-angular';
import type { EventCategory, EventType } from '../../core/models/middleware/event-item.model';
import type { NewsAtFourEnergyItemModel } from '../../core/models/news-at-four-energy-item.model';
import { EventService } from '../../core/services/event.service';
import { MicrosoftTeamsIntegrationService } from '../../core/services/microsoft-teams-integration.service';
import { getEventCategoryLabel, mapEventTypeToCategory } from '../../core/utils/event-category.util';
import { DatetimeInfoLineComponent } from '../../shared/components/datetime-info-line/datetime-info-line.component';
import { SharedCardComponent } from '../../shared/components/shared-card/shared-card.component';
import { EnforceNewLinePipe } from '../../shared/pipes/enforce-new-line.pipe';
import { UpdatesComponent } from '../updates/updates.component';

@Component({
  selector: 'app-news-at-four-energy-overview',
  standalone: true,
  imports: [LucideAngularModule, SharedCardComponent, UpdatesComponent, DatetimeInfoLineComponent, EnforceNewLinePipe],
  templateUrl: './news-at-four-energy-overview.component.html',
})
export class NewsAtFourEnergyOverviewComponent {
  // Icons
  readonly video = Video;
  readonly calendar = Calendar;
  readonly tag = Tag;
  readonly user = User;
  readonly chevronDown = ChevronDown;
  readonly chevronUp = ChevronUp;

  readonly eventType: EventType = 'NewsAtFourEnergy';
  readonly eventCategory: EventCategory = mapEventTypeToCategory(this.eventType);

  private route = inject(ActivatedRoute);
  private eventService = inject(EventService);
  private microsoftTeamsIntegrationService = inject(MicrosoftTeamsIntegrationService);

  // Event data
  eventDocumentId = this.route.snapshot.params.eventDocumentId as string;

  event: Signal<NewsAtFourEnergyItemModel | null> = computed(() =>
    this.eventService.getCurrentEvent()?.type === this.eventType
      ? (this.eventService.getCurrentEvent() as NewsAtFourEnergyItemModel)
      : null
  );

  // Description expansion state
  isDescriptionExpanded = signal(false);

  // Toggle description expansion
  toggleDescription() {
    this.isDescriptionExpanded.update((current) => !current);
  }

  // Computed property for description display
  displayDescription = computed(() => {
    const description = this.event()?.description || '';
    const maxLength = 150;

    if (this.isDescriptionExpanded() || description.length <= maxLength) {
      return description;
    }

    return `${description.substring(0, maxLength)}...`;
  });

  // Check if description needs expansion
  needsExpansion = computed(() => {
    const description = this.event()?.description || '';
    return description.length > 150;
  });

  // Signals for enabling join button
  now = signal(new Date());
  joinButtonActivated = computed(() => {
    if (!this.event()) {
      return false;
    }

    const now = this.now().getTime();
    const gracePeriod = 15 * 60 * 1000;
    const start = this.event()!.startDate.getTime() - gracePeriod;
    const end = this.event()!.endDate.getTime() + gracePeriod;

    return now >= start && now <= end;
  });

  constructor() {
    this.eventService.getEvent(this.eventDocumentId, this.eventType).subscribe({
      next: (data) => {
        if (data) {
          this.eventService.setCurrentEvent(data as NewsAtFourEnergyItemModel);
        } else {
          // If we don't receive event data, we need to clear old data, to prevent us from displaying the wrong data
          this.eventService.clearCurrentEvent();
        }
      },
      error: (error) => {
        this.eventService.clearCurrentEvent();
        // TODO [#215]: use winston for logging
        console.error(`Error loading ${this.eventType} event:`, error);
        throw error;
      },
    });

    // Refresh current time for signals every 5 seconds
    effect(() => {
      const intervalId = setInterval(() => {
        this.now.set(new Date());
      }, 5000);
      return () => clearInterval(intervalId);
    });
  }

  joinMeeting() {
    if (!!this.event()?.url && this.microsoftTeamsIntegrationService.isMicrosoftTeamsEnvironment()) {
      this.microsoftTeamsIntegrationService.joinMeeting(this.event()!.url!);
    } else {
      window.open(this.event()!.url, '_blank');
    }
  }

  protected readonly getEventCategoryLabel = getEventCategoryLabel;
}
